/**
 * 이상한 방 - 핀 칩 (순수 DOM)
 *
 * 근거 문서: 로드맵 확정안 3.1절(재화 - 핀) · 3.3절(0~300ms - 핀 칩에서 `-30`이 떨어진다)
 *           `gameHud.css` 머리말(시각 언어 - 먹색 플레이트 + 노랑 액센트)
 *
 * ## 칩 하나를 두 화면이 쓴다
 *
 * 단계 선택 화면의 머리말과 봉투 화면의 상단이 같은 칩을 단다. 두 곳이 각자 그리면 값이 같은데 모양이
 * 갈리고, 그러면 사람은 그것이 같은 값이라는 사실을 보지 못한다(`ui/dom.ts`의 `formatNumber` 주석과
 * 같은 결정). 그래서 칩은 위젯이고, 두 화면은 만들어서 끼워 넣기만 한다.
 *
 * ## 칩은 계산하지 않는다
 *
 * 받은 수를 그린다. 핀이 얼마나 오르고 내리는가는 `game/economy/pins.ts`가 정하고, 저장은 `main.ts`가
 * 한다. 여기서 `delta`를 받아 더하기 시작하면 두 번째 진실이 생긴다 - `drop`은 **표시**만 한다.
 *
 * ## 떨어지는 숫자의 정리는 타이머 하나다
 *
 * `-30`이 떨어지는 연출은 `juice.ts`의 규약을 그대로 따른다: `animationend`를 듣지 않고 타이머 하나가
 * 노드를 치운다. 움직임 줄이기에서는 애니메이션이 안 돌아 그 이벤트가 오지 않고, 그러면 노드가 영원히 남는다.
 */

import { onLocaleChange, t } from '../i18n'

import { createElement, formatNumber, restartAnimation, setAttr, setText } from './dom'

import './pinsChip.css'

/* --- 길이 ---------------------------------------------------------------------- */

/**
 * 떨어지는 숫자가 화면에 머무는 시간(ms). 봉투 연출의 첫 구간(0~300ms)보다 조금 길게 두어
 * 봉투가 자리 잡는 동안 "값이 빠졌다"가 함께 읽히게 한다. 1초를 넘기면 다음 칸의 연출과 겹친다.
 */
export const PINS_DROP_MS = 700

/** 숫자가 튀는(bump) 시간. 값이 오를 때만 튀고, 내릴 때는 떨어지는 숫자가 그 일을 한다. */
const BUMP_MS = 260

/* --- 타입 ---------------------------------------------------------------------- */

export type PinsChipHandle = Readonly<{
  root: HTMLElement
  /** 보유량을 그린다. 같은 값이면 DOM을 건드리지 않는다. */
  set: (count: number) => void
  /**
   * 값이 빠졌음을 보여 준다(`-30`). **보유량은 바꾸지 않는다** - `set`이 따로 새 값을 받는다.
   * 0 이하의 델타는 그릴 것이 없으므로 무시한다.
   */
  drop: (amount: number) => void
  /** 값이 올랐음을 한 번 튕겨 보여 준다. `set` 뒤에 부른다. */
  bump: () => void
  dispose: () => void
}>

/* --- 본체 ---------------------------------------------------------------------- */

/**
 * 핀 칩을 만든다. 부모에 붙이는 것은 호출자의 몫이다(`root`를 내준다) - 두 화면의 배치가 다르기 때문이다.
 */
export function createPinsChip(): PinsChipHandle {
  const root = createElement('div', 'pc-root')
  root.setAttribute('role', 'status')

  const icon = createElement('span', 'pc-icon')
  icon.setAttribute('aria-hidden', 'true')
  const count = createElement('strong', 'pc-count', '0')
  const label = createElement('span', 'pc-label', t('pins.chip.label'))
  root.append(icon, count, label)

  /*
   * 이름표("핀" / "Pins")는 **만들 때 한 번 쓰고 끝**이었다. 값(`set`)은 부를 때마다 다시 칠해지는데
   * 이름표를 부르는 자리가 없어서, 설정에서 언어를 바꾸면 이 한 줄만 옛 언어로 남았다 - 사물함에서
   * "핀 9,804개, 봉투 87장 열었다" 옆에 "9,804 Pins"가 서 있었다. 이 칩은 화면을 오가도 살아남는
   * 위젯이라(두 화면이 같은 것을 쓴다) 다시 그려질 기회가 없다. `connection.ts`와 같은 규약으로 직접 듣는다.
   */
  const stopLocaleWatch = onLocaleChange(() => setText(label, t('pins.chip.label')))

  /** 떨어지는 숫자와 튕김의 예약. 정리 권한은 이 둘뿐이다(`juice.ts` 규약). */
  let dropTimer: number | null = null
  let bumpTimer: number | null = null
  let dropNode: HTMLElement | null = null

  function clearDrop(): void {
    if (dropTimer !== null) {
      window.clearTimeout(dropTimer)
      dropTimer = null
    }
    dropNode?.remove()
    dropNode = null
  }

  function clearBump(): void {
    if (bumpTimer !== null) {
      window.clearTimeout(bumpTimer)
      bumpTimer = null
    }
    count.classList.remove('is-bump')
  }

  return {
    root,

    set(value: number): void {
      const safe = Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0
      setText(count, formatNumber(safe))
      setAttr(root, 'aria-label', t('pins.chip.aria', { count: safe }))
    },

    drop(amount: number): void {
      if (!Number.isFinite(amount) || amount <= 0) return
      // 앞의 것이 아직 떠 있으면 먼저 치운다. 연타로 숫자가 쌓이면 그것 자체가 화면을 덮는 길이다.
      clearDrop()

      const node = createElement('span', 'pc-drop', `-${formatNumber(Math.floor(amount))}`)
      node.setAttribute('aria-hidden', 'true')
      root.append(node)
      dropNode = node
      dropTimer = window.setTimeout(clearDrop, PINS_DROP_MS)
    },

    bump(): void {
      clearBump()
      restartAnimation(count)
      count.classList.add('is-bump')
      bumpTimer = window.setTimeout(clearBump, BUMP_MS)
    },

    dispose(): void {
      clearDrop()
      clearBump()
      stopLocaleWatch()
      root.remove()
    },
  }
}
